import { useState, useRef } from "react";
import { Upload, Trash2, Loader2, ImagePlus } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

export default function AdminImageUpload({ releaseId, currentImage, onImageUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File too large (max 10MB)");
      return;
    }

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, WebP, GIF allowed");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/releases/${releaseId}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded!");
      onImageUpdate?.(data.image_url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/releases/${releaseId}/image`);
      toast.success("Image removed");
      onImageUpdate?.(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove image");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="admin-image-upload" className="flex items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleUpload}
        data-testid="image-file-input"
      />
      <Button
        data-testid="upload-image-btn"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        size="sm"
        className="bg-rose-600 hover:bg-rose-500 text-white border-0"
      >
        {uploading ? (
          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading...</>
        ) : (
          <><ImagePlus className="w-3.5 h-3.5 mr-1.5" /> {currentImage ? "Replace Photo" : "Upload Photo"}</>
        )}
      </Button>
      {currentImage && (
        <Button
          data-testid="delete-image-btn"
          onClick={handleDelete}
          disabled={deleting}
          size="sm"
          variant="outline"
          className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-rose-400"
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </Button>
      )}
    </div>
  );
}
